import React, { useRef, useState, useEffect, useContext, useCallback } from 'react';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { TabView, TabPanel } from 'primereact/tabview';
import { Checkbox } from 'primereact/checkbox';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import axios from 'axios';
import { BarrioContext } from '../components/BarrioContext';
import { useAuth } from '../components/AuthContext';
import LoadingOverlay from '../components/LoadingOverlay';
import AsambleaArchivos from '../components/AsambleaArchivos';
import {
    getAsambleas, saveAsamblea, deleteAsamblea,
    getAsistentes, saveAsistente,
    getTemas, saveTema, deleteTema,
} from '../service/ComunidadService';

const HOST_API = process.env.REACT_APP_URL;

const TIPOS = ['Ordinaria', 'Extraordinaria'];
const MODALIDADES = ['Presencial', 'Virtual', 'Mixta'];
const ESTADOS = ['Programada', 'Realizada', 'Cancelada'];
const RESULTADOS = ['Pendiente', 'Aprobado', 'Rechazado'];

const ESTADO_COLORS = {
    Programada: { bg: '#eff8ff', color: '#1570ef', border: '#b2ddff' },
    Realizada:  { bg: '#ecfdf3', color: '#12b76a', border: '#a9efc5' },
    Cancelada:  { bg: '#fef3f2', color: '#f04438', border: '#fecdca' },
};

const estadoBadge = (estado) => {
    const s = ESTADO_COLORS[estado] || { bg: '#f2f4f7', color: '#667085', border: '#d0d5dd' };
    return (
        <span style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}`, fontSize: '0.78rem', fontWeight: 700, padding: '0.1rem 0.55rem', borderRadius: 99 }}>
            {estado}
        </span>
    );
};

const fmtFecha = (d) => d ? new Date(d).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';

const FORM_EMPTY = {
    asambleaId: 0, fecha: new Date(), tipo: 'Ordinaria', modalidad: 'Presencial', lugar: '', estado: 'Programada', notas: '',
};

const TEMA_EMPTY = {
    asambleaTemaId: 0, titulo: '', descripcion: '', votosAFavor: 0, votosEnContra: 0, votosAbstencion: 0, resultado: 'Pendiente',
};

const ComunidadAsambleasAdmin = () => {
    const toast = useRef(null);
    const { barrio } = useContext(BarrioContext);
    const { administracionId, usuarioId, rolId } = useAuth();

    const [asambleas, setAsambleas] = useState([]);
    const [loading, setLoading] = useState(false);

    const [modalVisible, setModalVisible] = useState(false);
    const [form, setForm] = useState({ ...FORM_EMPTY });
    const [submitting, setSubmitting] = useState(false);

    const [detalleVisible, setDetalleVisible] = useState(false);
    const [asambleaDetalle, setAsambleaDetalle] = useState(null);
    const [asistentes, setAsistentes] = useState([]);
    // Personas vigentes de cada UF ({ [unidadFuncionalId]: [{ personaId, nombreCompleto }] }),
    // cargadas a demanda: el combo "Representada por" solo ofrece las de esa UF.
    const [personasPorUF, setPersonasPorUF] = useState({});
    const personasPorUFRef = useRef({});
    const [temas, setTemas] = useState([]);
    const [loadingDetalle, setLoadingDetalle] = useState(false);

    const [temaForm, setTemaForm] = useState({ ...TEMA_EMPTY });
    const [guardandoTema, setGuardandoTema] = useState(false);

    const fetchAsambleas = useCallback(async () => {
        if (!barrio?.id) return;
        setLoading(true);
        try {
            const { data } = await getAsambleas(administracionId, barrio.id);
            setAsambleas(data);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar las asambleas', life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [barrio?.id, administracionId]);

    useEffect(() => { fetchAsambleas(); }, [fetchAsambleas]);

    const cargarPersonasUF = async (unidadFuncionalId) => {
        if (personasPorUFRef.current[unidadFuncionalId]) return personasPorUFRef.current[unidadFuncionalId];
        try {
            const { data } = await axios.get(`${HOST_API}api/unidadFuncionalPersona/GetHistorialUF`, { params: { unidadFuncionalId } });
            const vigentes = (data || []).filter(p => !p.fechaHasta);
            const lista = [...new Map(vigentes.map(p => [p.personaId, { personaId: p.personaId, nombreCompleto: p.nombreCompleto }])).values()];
            personasPorUFRef.current = { ...personasPorUFRef.current, [unidadFuncionalId]: lista };
            setPersonasPorUF(personasPorUFRef.current);
            return lista;
        } catch {
            return [];
        }
    };

    // ─── Alta / edición ─────────────────────────────────────────────────────────

    const abrirNueva = () => { setForm({ ...FORM_EMPTY }); setModalVisible(true); };

    const editar = (row) => {
        setForm({ ...row, fecha: new Date(row.fecha) });
        setModalVisible(true);
    };

    const guardar = async () => {
        if (!form.fecha || !form.tipo || !form.estado) {
            toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Completá fecha, tipo y estado', life: 3000 });
            return;
        }
        setSubmitting(true);
        try {
            await saveAsamblea({ ...form, barrioId: barrio.id, administracionId, usuarioCreacionId: usuarioId });
            toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Asamblea guardada correctamente', life: 3000 });
            setModalVisible(false);
            fetchAsambleas();
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo guardar la asamblea', life: 4000 });
        } finally {
            setSubmitting(false);
        }
    };

    const eliminar = (row) => {
        confirmDialog({
            message: `¿Eliminar la asamblea del ${fmtFecha(row.fecha)}?`,
            header: 'Confirmar eliminación',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Eliminar',
            rejectLabel: 'Cancelar',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await deleteAsamblea(row.asambleaId, administracionId);
                    fetchAsambleas();
                } catch {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo eliminar la asamblea', life: 3000 });
                }
            },
        });
    };

    // ─── Detalle: asistencia + temas ────────────────────────────────────────────

    const abrirDetalle = async (row) => {
        setAsambleaDetalle(row);
        setDetalleVisible(true);
        setLoadingDetalle(true);
        try {
            const [a, t] = await Promise.all([getAsistentes(row.asambleaId), getTemas(row.asambleaId)]);
            setAsistentes(a.data);
            setTemas(t.data);
            // Para las UF que ya tienen representante guardado, traer sus personas así el combo muestra el nombre
            await Promise.all((a.data || []).filter(x => x.personaId).map(x => cargarPersonasUF(x.unidadFuncionalId)));
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo cargar el detalle', life: 3000 });
        } finally {
            setLoadingDetalle(false);
        }
    };

    const togglePresente = async (row, presente) => {
        let personaId = row.personaId || null;
        // Al marcarla presente, si la UF tiene una sola persona vigente, se toma como representante
        if (presente && !personaId) {
            const personasUF = await cargarPersonasUF(row.unidadFuncionalId);
            if (personasUF.length === 1) personaId = personasUF[0].personaId;
        }
        setAsistentes(prev => prev.map(a => a.unidadFuncionalId === row.unidadFuncionalId ? { ...a, presente, personaId } : a));
        try {
            await saveAsistente({ asambleaId: asambleaDetalle.asambleaId, unidadFuncionalId: row.unidadFuncionalId, personaId, presente });
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo guardar la asistencia', life: 3000 });
        }
    };

    const cambiarPersonaAsistente = async (row, personaId) => {
        setAsistentes(prev => prev.map(a => a.unidadFuncionalId === row.unidadFuncionalId ? { ...a, personaId } : a));
        try {
            await saveAsistente({ asambleaId: asambleaDetalle.asambleaId, unidadFuncionalId: row.unidadFuncionalId, personaId: personaId || null, presente: row.presente });
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo guardar la asistencia', life: 3000 });
        }
    };

    const presentes = asistentes.filter(a => a.presente).length;

    const abrirNuevoTema = () => setTemaForm({ ...TEMA_EMPTY });

    const editarTema = (row) => setTemaForm({ ...row });

    const guardarTema = async () => {
        if (!temaForm.titulo) {
            toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Ingresá el título del tema', life: 3000 });
            return;
        }
        setGuardandoTema(true);
        try {
            await saveTema({ ...temaForm, asambleaId: asambleaDetalle.asambleaId });
            const { data } = await getTemas(asambleaDetalle.asambleaId);
            setTemas(data);
            setTemaForm({ ...TEMA_EMPTY });
            toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Tema guardado correctamente', life: 2500 });
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo guardar el tema', life: 3000 });
        } finally {
            setGuardandoTema(false);
        }
    };

    const eliminarTema = (row) => {
        confirmDialog({
            message: `¿Eliminar el tema "${row.titulo}"?`,
            header: 'Confirmar eliminación',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Eliminar',
            rejectLabel: 'Cancelar',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await deleteTema(row.asambleaTemaId);
                    setTemas(prev => prev.filter(t => t.asambleaTemaId !== row.asambleaTemaId));
                } catch {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo eliminar el tema', life: 3000 });
                }
            },
        });
    };

    // ─── Render ───────────────────────────────────────────────────────────────

    return (
        <>
            <h4>Asambleas</h4>
            <br />
            <ConfirmDialog />
            <Toast ref={toast} />
            <LoadingOverlay visible={loading} />

            <div className="flex align-items-center gap-3 mb-3">
                <Button label="Nueva Asamblea" icon="pi pi-plus" className="p-button-sm" onClick={abrirNueva} />
            </div>

            <div className="grid">
                <div className="col-12">
                    <DataTable value={asambleas} paginator rows={15} rowsPerPageOptions={[15, 25, 50]}
                        showGridlines responsiveLayout="scroll"
                        emptyMessage="No hay asambleas cargadas"
                        className="p-datatable-gridlines bg-white">
                        <Column field="fecha" header="Fecha" sortable body={(r) => fmtFecha(r.fecha)} style={{ width: '9rem' }} />
                        <Column field="tipo" header="Tipo" sortable style={{ width: '10rem' }} />
                        <Column field="lugar" header="Lugar / Modalidad" body={(r) => [r.lugar, r.modalidad].filter(Boolean).join(' — ') || '—'} />
                        <Column header="Estado" body={(r) => estadoBadge(r.estado)} style={{ width: '9rem' }} />
                        <Column header="Acciones" style={{ width: '10rem', textAlign: 'center' }} body={(row) => (
                            <div className="flex gap-2 justify-content-center">
                                <Button icon="pi pi-list" className="p-button-rounded p-button-text p-button-sm" tooltip="Asistencia y temas" onClick={() => abrirDetalle(row)} />
                                <Button icon="pi pi-pencil" className="p-button-rounded p-button-text p-button-sm" severity="warning" tooltip="Editar" onClick={() => editar(row)} />
                                <Button icon="pi pi-trash" className="p-button-rounded p-button-text p-button-sm" severity="danger" tooltip="Eliminar" onClick={() => eliminar(row)} />
                            </div>
                        )} />
                    </DataTable>
                </div>
            </div>

            {/* ─── Modal: Nueva / Editar Asamblea ──────────────────────────────── */}
            <Dialog header={form.asambleaId ? 'Editar Asamblea' : 'Nueva Asamblea'} visible={modalVisible} style={{ width: '520px' }} modal
                onHide={() => setModalVisible(false)}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancelar" className="p-button-text p-button-sm" onClick={() => setModalVisible(false)} disabled={submitting} />
                        <Button label="Guardar" icon="pi pi-check" className="p-button-sm" onClick={guardar} loading={submitting} />
                    </div>
                }>
                <div className="grid p-fluid mt-1">
                    <div className="col-6">
                        <label style={lbl}>Fecha *</label>
                        <Calendar value={form.fecha} onChange={(e) => setForm(f => ({ ...f, fecha: e.value }))}
                            dateFormat="dd/mm/yy" showTime hourFormat="24" showIcon className="p-inputtext-sm" />
                    </div>
                    <div className="col-6">
                        <label style={lbl}>Tipo *</label>
                        <Dropdown value={form.tipo} options={TIPOS} onChange={(e) => setForm(f => ({ ...f, tipo: e.value }))} className="p-inputtext-sm" />
                    </div>
                    <div className="col-6">
                        <label style={lbl}>Modalidad</label>
                        <Dropdown value={form.modalidad} options={MODALIDADES} onChange={(e) => setForm(f => ({ ...f, modalidad: e.value }))} showClear className="p-inputtext-sm" />
                    </div>
                    <div className="col-6">
                        <label style={lbl}>Estado *</label>
                        <Dropdown value={form.estado} options={ESTADOS} onChange={(e) => setForm(f => ({ ...f, estado: e.value }))} className="p-inputtext-sm" />
                    </div>
                    <div className="col-12">
                        <label style={lbl}>Lugar</label>
                        <InputText value={form.lugar || ''} onChange={(e) => setForm(f => ({ ...f, lugar: e.target.value }))} className="p-inputtext-sm" />
                    </div>
                    <div className="col-12">
                        <label style={lbl}>Notas</label>
                        <InputTextarea value={form.notas || ''} onChange={(e) => setForm(f => ({ ...f, notas: e.target.value }))} rows={4} className="p-inputtext-sm" />
                    </div>
                </div>
            </Dialog>

            {/* ─── Modal: Detalle (Asistencia + Temas) ─────────────────────────── */}
            <Dialog header={asambleaDetalle ? `Asamblea del ${fmtFecha(asambleaDetalle.fecha)}` : ''} visible={detalleVisible}
                style={{ width: '900px' }} breakpoints={{ '960px': '95vw' }} modal onHide={() => setDetalleVisible(false)}>
                <LoadingOverlay visible={loadingDetalle} />
                <TabView>
                    <TabPanel header={`Asistencia (${presentes}/${asistentes.length})`}>
                        <DataTable value={asistentes} showGridlines responsiveLayout="scroll" className="p-datatable-sm" emptyMessage="Sin unidades funcionales">
                            <Column field="unidadFuncional" header="Unidad Funcional" />
                            <Column header="Presente" style={{ width: '7rem', textAlign: 'center' }} body={(row) => (
                                <Checkbox checked={row.presente} onChange={(e) => togglePresente(row, e.checked)} />
                            )} />
                            <Column header="Representada por" body={(row) => (
                                <Dropdown value={row.personaId || null} options={personasPorUF[row.unidadFuncionalId] || []}
                                    optionLabel="nombreCompleto" optionValue="personaId"
                                    placeholder="—" showClear className="p-inputtext-sm" style={{ width: '100%' }}
                                    emptyMessage={personasPorUF[row.unidadFuncionalId] ? 'La UF no tiene personas asignadas' : 'Cargando...'}
                                    onShow={() => cargarPersonasUF(row.unidadFuncionalId)}
                                    onChange={(e) => cambiarPersonaAsistente(row, e.value)} disabled={!row.presente} />
                            )} />
                        </DataTable>
                    </TabPanel>
                    <TabPanel header={`Temas votados (${temas.length})`}>
                        <div className="grid p-fluid mb-3" style={{ background: '#f9fafb', padding: '0.75rem', borderRadius: 8 }}>
                            <div className="col-12">
                                <label style={lbl}>Título</label>
                                <InputText value={temaForm.titulo} onChange={(e) => setTemaForm(f => ({ ...f, titulo: e.target.value }))} className="p-inputtext-sm" />
                            </div>
                            <div className="col-12">
                                <label style={lbl}>Descripción</label>
                                <InputTextarea value={temaForm.descripcion || ''} onChange={(e) => setTemaForm(f => ({ ...f, descripcion: e.target.value }))} rows={2} className="p-inputtext-sm" />
                            </div>
                            <div className="col-4">
                                <label style={lbl}>Votos a favor</label>
                                <InputNumber value={temaForm.votosAFavor} onValueChange={(e) => setTemaForm(f => ({ ...f, votosAFavor: e.value || 0 }))} className="p-inputtext-sm" />
                            </div>
                            <div className="col-4">
                                <label style={lbl}>Votos en contra</label>
                                <InputNumber value={temaForm.votosEnContra} onValueChange={(e) => setTemaForm(f => ({ ...f, votosEnContra: e.value || 0 }))} className="p-inputtext-sm" />
                            </div>
                            <div className="col-4">
                                <label style={lbl}>Abstenciones</label>
                                <InputNumber value={temaForm.votosAbstencion} onValueChange={(e) => setTemaForm(f => ({ ...f, votosAbstencion: e.value || 0 }))} className="p-inputtext-sm" />
                            </div>
                            <div className="col-6">
                                <label style={lbl}>Resultado</label>
                                <Dropdown value={temaForm.resultado} options={RESULTADOS} onChange={(e) => setTemaForm(f => ({ ...f, resultado: e.value }))} className="p-inputtext-sm" />
                            </div>
                            <div className="col-6 flex align-items-end gap-2">
                                <Button label={temaForm.asambleaTemaId ? 'Guardar cambios' : 'Agregar tema'} icon="pi pi-check" className="p-button-sm" onClick={guardarTema} loading={guardandoTema} />
                                {temaForm.asambleaTemaId ? <Button label="Cancelar" className="p-button-text p-button-sm" onClick={abrirNuevoTema} /> : null}
                            </div>
                        </div>
                        <DataTable value={temas} showGridlines responsiveLayout="scroll" className="p-datatable-sm" emptyMessage="Sin temas cargados">
                            <Column field="titulo" header="Título" />
                            <Column header="A favor" style={{ width: '6rem', textAlign: 'center' }} body={(r) => r.votosAFavor} />
                            <Column header="En contra" style={{ width: '6rem', textAlign: 'center' }} body={(r) => r.votosEnContra} />
                            <Column header="Abstención" style={{ width: '7rem', textAlign: 'center' }} body={(r) => r.votosAbstencion} />
                            <Column header="Resultado" style={{ width: '8rem' }} body={(r) => r.resultado} />
                            <Column header="Acciones" style={{ width: '6rem', textAlign: 'center' }} body={(row) => (
                                <div className="flex gap-2 justify-content-center">
                                    <Button icon="pi pi-pencil" className="p-button-rounded p-button-text p-button-sm" onClick={() => editarTema(row)} />
                                    <Button icon="pi pi-trash" className="p-button-rounded p-button-text p-button-sm" severity="danger" onClick={() => eliminarTema(row)} />
                                </div>
                            )} />
                        </DataTable>
                    </TabPanel>
                    <TabPanel header="Actas y documentos">
                        {asambleaDetalle && (
                            <AsambleaArchivos
                                asamblea={asambleaDetalle}
                                administracionId={administracionId}
                                barrioId={barrio?.id}
                                usuarioId={usuarioId}
                                rolId={rolId}
                                toast={toast}
                            />
                        )}
                    </TabPanel>
                </TabView>
            </Dialog>
        </>
    );
};

const lbl = { display: 'block', marginBottom: '0.35rem', fontWeight: 500, color: '#344054', fontSize: '0.87rem' };

export default ComunidadAsambleasAdmin;
